/**
 * Newsroom — shared data source.
 *
 * Consumed by:
 *  · components/landing/Newsroom.jsx (homepage · headline)
 *  · pages/NewsPage.jsx (/news archive · title + body, grouped by quarter)
 *  · pages/NewsDetailPage.jsx (/news/:slug · full release)
 *
 * Slugs are public URLs — keep stable. Order: newest first.
 */
export const NEWS = [
  {
    slug: "family-office-investment-solutions-launched",
    date: "Q2 2026",
    kind: "Capital Pathways",
    headline:
      "Family Office Investment Solutions launched — a single global service supporting select family offices and strategic capital partners across all jurisdictions.",
    title: "Family Office Investment Solutions launched.",
    body:
      "The partnership has launched Family Office Investment Solutions, a single global service supporting select family offices and strategic capital partners seeking disciplined access to private-market opportunities. The service is documented within the platform's Selective Capital Pathways framework and is delivered across all jurisdictions through the firm's existing regional network. Each engagement is tailored to the family's mandate, governance requirements and jurisdiction; the family retains full discretion over all investment decisions.",
    detail: [
      "The service follows a three-stage process — Define the Mandate, Screen & Structure, Support Execution. The partnership works with each family to understand its investment objectives, screens and evaluates aligned opportunities, coordinates commercial and financial diligence, and develops appropriate transaction structures for investment committee consideration, with legal, tax, regulatory and specialist advisers coordinated where required.",
      "Family-office mandates are received through the partnership's confidential enquiry process under a dedicated Family Office Mandate subject. Any engagement remains subject to diligence, suitability, jurisdictional requirements and final approval by the relevant capital provider; Kellermann Reid & Co. does not act as a placement agent or broker-dealer, and does not guarantee funding or transaction outcomes.",
    ],
    related: [
      { label: "Family Office Investment Solutions", to: "/family-office-investment-solutions" },
      { label: "Selective Capital Pathways", to: "/capital-pathways" },
      { label: "Ryan Landry · Partner, Strategic Capital", to: "/partnership#ryan-landry" },
    ],
  },
  {
    slug: "gulf-investor-engagement-note-published",
    date: "Q2 2026",
    kind: "Investor Relations",
    headline:
      "Gulf investor engagement note published, with an Arabic-language summary, setting out how UAE and GCC limited partners engage with the platform.",
    title:
      "Gulf investor engagement note published, with Arabic-language summary.",
    body:
      "The partnership has published a dedicated engagement note for Gulf investors, written by Gulf Region Lead Sven Massenhove, setting out how limited partners in the United Arab Emirates and the wider GCC engage with the platform — by introduction, through a named regional contact in Dubai (DIFC), and on the same documented terms as every institutional relationship the firm holds. The note opens with an Arabic-language summary for regional readers; formal correspondence remains in English.",
    detail: [
      "The note describes the four categories of Gulf capital the partnership engages — sovereign-adjacent and institutional allocators, single- and multi-family offices, private banks and DIFC-based advisers, and strategic or operating groups — together with a four-stage engagement path from an introductory conversation in Dubai to documentation and funding concluded by the partnership.",
      "Consistent with the platform's Selective Capital Pathways framework, the note records that Kellermann Reid & Co. is not licensed or regulated in the United Arab Emirates or the GCC, that regional coverage is introductory, and that nothing published constitutes a financial promotion or an offer of securities in the region.",
    ],
    related: [
      { label: "For Gulf Investors", to: "/gulf-investors" },
      { label: "Sven Massenhove · Gulf Region Lead", to: "/regional-coverage#sven-massenhove" },
      { label: "Selective Capital Pathways", to: "/capital-pathways" },
    ],
  },
  {
    slug: "dubai-difc-gulf-coverage",
    date: "Q2 2026",
    kind: "Global Footprint",
    headline:
      "Gulf Region coverage established in Dubai (DIFC), extending the platform's footprint to the UAE and wider GCC.",
    title: "Gulf Region coverage established in Dubai (DIFC).",
    body:
      "The partnership has established Gulf Region coverage in Dubai, anchored in the Dubai International Financial Centre, extending the platform's footprint to the United Arab Emirates and the wider GCC. The coverage supports institutional, sovereign-adjacent and family-office dialogue in the region, together with life-sciences and healthcare origination — and is led on the ground by Gulf Region Lead Sven Massenhove, with introductions leading back to the partnership's Capital Formation function. Dubai joins New York, London, Geneva, Singapore and Hong Kong as the platform's sixth coverage location alongside its Austin headquarters.",
    detail: [
      "Gulf Region coverage operates under the partnership's documented Selective Capital Pathways framework. It does not constitute a regulated office, branch or licensed activity in the United Arab Emirates; regional dialogue is conducted on an introductory basis and any investment engagement is documented and concluded by the partnership directly.",
    ],
    related: [
      { label: "For Gulf Investors", to: "/gulf-investors" },
      { label: "Global Footprint", to: "/#offices" },
      { label: "Sven Massenhove · Gulf Region Lead", to: "/regional-coverage#sven-massenhove" },
      { label: "Selective Capital Pathways", to: "/capital-pathways" },
    ],
  },
  {
    slug: "sven-massenhove-senior-advisor-gulf-region-lead",
    date: "Q2 2026",
    kind: "Regional Coverage",
    headline:
      "Sven Massenhove appointed Gulf Region Lead and Senior Advisor for Life Sciences & Pharma, establishing the platform's on-the-ground investor coverage in the UAE.",
    title:
      "Sven Massenhove appointed Gulf Region Lead and Senior Advisor for Life Sciences & Pharma.",
    body:
      "Sven Massenhove, Founder and Chief Executive Officer of Dubai-based BioNexium, has been appointed Gulf Region Lead, establishing the platform's Regional Coverage tier and serving as its on-the-ground contact point for investor relationships across the United Arab Emirates and the wider GCC. He additionally serves as Senior Advisor for Life Sciences & Pharma. Regional leads engage on documented, non-executive terms; investment discussions and documentation remain with the partnership directly.",
    detail: [
      "Sven founded and leads BioNexium's two proprietary platforms — StemRevive-X, an autologous cell-based regenerative framework, and Quantum BioForge, a computational platform modelling protein-protein interactions to identify high-affinity therapeutic peptide sequences. He brings more than six years of executive leadership in biotechnology and a working, on-the-ground understanding of the Gulf's institutional and family-office capital landscape.",
      "He contributes to the platform's Perspectives series, including the paper \u201cHealthcare & life-sciences credit: underwriting the asset, not the trial.\u201d",
    ],
    related: [
      { label: "Sven Massenhove · Full Profile", to: "/regional-coverage#sven-massenhove" },
      { label: "Paper · Healthcare & life-sciences credit", to: "/perspectives/healthcare-life-sciences-credit" },
    ],
  },
  {
    slug: "senior-advisors-alistair-wood-angelo-barbati",
    date: "Q1 2026",
    kind: "Senior Advisors",
    headline:
      "Alistair Wood and Angelo Barbati appointed to the platform's Senior Advisor tier, extending its operating and capital-markets counsel.",
    title:
      "Alistair Wood and Angelo Barbati appointed to the platform's Senior Advisor tier.",
    body:
      "Alistair Wood and Angelo Barbati have been appointed to the Senior Advisor tier, extending the partnership's operating and capital-markets counsel. Senior Advisors engage with the platform on a documented advisory basis and do not hold general-partner economics.",
    detail: [
      "Alistair Wood, formerly State Manager, New South Wales for HSBC Australia's Commercial Banking operations, contributes corporate-banking and syndicated-credit pattern recognition across bi-lateral, club and syndicated facilities. Angelo Barbati, formerly Senior Manager, Middle Market Enterprises at HSBC Bank Australia, brings twenty-five years of debt-structuring, trade-finance, treasury and credit-risk experience across ANZ, Banca Monte Paschi and HSBC.",
    ],
    related: [{ label: "Senior Advisor Profiles", to: "/advisors" }],
  },
  {
    slug: "sophus-kludt-head-of-capital-formation",
    date: "Q1 2026",
    kind: "Leadership",
    headline:
      "Sophus Kludt appointed Head of Capital Formation; Executive Leadership tier established to support the partnership on institutional and family-office capital-raising.",
    title:
      "Sophus Kludt appointed Head of Capital Formation; Executive Leadership tier established.",
    body:
      "Sophus Kludt has been appointed Head of Capital Formation, leading capital-raising initiatives for KR-sponsored funds and special-purpose vehicles. His appointment establishes the platform's Executive Leadership tier — senior executives who lead a defined function in support of the partnership.",
    related: [{ label: "Sophus Kludt · Full Profile", to: "/leadership#sophus-kludt" }],
  },
  {
    slug: "ryan-landry-partner-strategic-capital",
    date: "Q1 2026",
    kind: "Partnership",
    headline:
      "Ryan Landry appointed Partner for Strategic Capital, anchoring the platform's engagement with family offices and strategic capital partners.",
    title: "Ryan Landry appointed Partner for Strategic Capital.",
    body:
      "Ryan Landry has been appointed Partner for Strategic Capital, anchoring the platform's engagement with family offices, single- and multi-family office capital partners and global investment partner relationships. Ryan brings a background across capital fundraising, mergers and acquisitions and business transformation to the partnership.",
    related: [{ label: "Ryan Landry · Full Profile", to: "/partnership#ryan-landry" }],
  },
  {
    slug: "rachel-yang-partner-asia-pacific",
    date: "Q1 2026",
    kind: "Partnership",
    headline:
      "Rachel Yang appointed Partner for Asia-Pacific, leading the platform's regional origination and investor coverage.",
    title: "Rachel Yang appointed Partner for Asia-Pacific.",
    body:
      "Rachel Yang has been appointed Partner for Asia-Pacific, leading the platform's regional origination and investor coverage. Rachel brings deep credit underwriting and bespoke client structuring experience from major banking institutions and anchors the partnership's engagement with Asia-Pacific limited partners and family offices.",
    related: [{ label: "Rachel Yang · Full Profile", to: "/partnership#rachel-yang" }],
  },
  {
    slug: "investor-relations-desk-established",
    date: "Q1 2026",
    kind: "Investor Relations",
    headline:
      "Dedicated Investor Relations desk established; institutional and family-office enquiries routed to ir@krprivatecapital.com.",
    title: "Dedicated Investor Relations desk established.",
    body:
      "A dedicated Investor Relations desk has been established to receive institutional and family-office enquiries. Documented enquiries are routed to ir@krprivatecapital.com and coordinated with the partnership on a considered, by-introduction basis.",
    related: [{ label: "Contact the Partnership", to: "/contact" }],
  },
  {
    slug: "selective-capital-pathways-framework",
    date: "Q1 2026",
    kind: "Capital Pathways",
    headline:
      "Selective Capital Pathways framework published — a mandate-led engagement architecture across four documented pathways for strategic and institutional capital.",
    title: "Selective Capital Pathways framework published.",
    body:
      "The partnership has published its Selective Capital Pathways framework — a mandate-led engagement architecture across four documented pathways (Direct Platform Capital, Investor Access, Fund-Structured Solutions and Coordinated Introductions). The framework formalises the platform's compliance-conscious posture toward strategic and institutional capital.",
    related: [
      { label: "Read the Pathways Framework", to: "/capital-pathways" },
      { label: "Disclosures", to: "/disclosures" },
    ],
  },
  {
    slug: "austin-headquarters-established",
    date: "Q1 2026",
    kind: "Headquarters",
    headline:
      "Firm headquarters established in Austin, Texas — anchoring the partnership's North American operating base.",
    title: "Firm headquarters established in Austin, Texas.",
    body:
      "The partnership has established its headquarters in Austin, Texas, anchoring the firm's North American operating base alongside its international coverage across London, Geneva and the Asia-Pacific region.",
    related: [{ label: "Global Footprint", to: "/#offices" }],
  },
  {
    slug: "anchor-dialogue-initiated",
    date: "Q1 2026",
    kind: "Anchor Dialogue",
    headline:
      "Confidential dialogue initiated with the platform's first strategic seed and anchor counterparties.",
    title:
      "Confidential dialogue initiated with the platform's first strategic seed and anchor counterparties.",
    body:
      "The partnership has opened a confidential dialogue with a small number of strategic capital partners — including family offices and select institutional counterparties — evaluating an anchor allocation to the platform.",
    related: [{ label: "Strategic Seed / Anchor Partner Opportunity", to: "/#anchor-partner" }],
  },
  {
    slug: "cv5-capital-cayman-platform",
    date: "Q1 2026",
    kind: "Cayman Platform",
    headline:
      "Platform engagement discussions advanced with CV5 Capital (CV5 SPC Cayman Islands fund platform) for the firm's Cayman fund infrastructure.",
    title:
      "Platform engagement discussions advanced with CV5 Capital (CV5 SPC) as the firm's Cayman fund infrastructure.",
    body:
      "CV5 Capital's CV5 SPC Cayman Islands fund platform is in advanced engagement discussions to provide the institutional Cayman fund infrastructure for Kellermann Reid & Co.'s vehicle architecture.",
    related: [{ label: "Fund Architecture", to: "/funds" }],
  },
  {
    slug: "bell-rock-group-cayman-governance",
    date: "Q1 2026",
    kind: "Governance",
    headline:
      "Engagement discussions advanced with Bell Rock Group (Cayman Islands) for Cayman governance, registered-office coordination and corporate-services support.",
    title:
      "Engagement discussions advanced with Bell Rock Group (Cayman) for governance and corporate-services support.",
    body:
      "Bell Rock Group is in engagement discussions with the platform for Cayman governance, registered-office coordination and corporate-services support — completing the institutional service-provider stack.",
    related: [{ label: "Disclosures", to: "/disclosures" }],
  },
  {
    slug: "nine-vehicle-platform-architecture",
    date: "Q1 2026",
    kind: "Vehicle Architecture",
    headline:
      "Nine-vehicle platform architecture finalised across funds, co-investment, business lines and separately managed accounts.",
    title:
      "Nine-vehicle platform architecture finalised across funds, co-investment, business lines and SMAs.",
    body:
      "The platform's intended vehicle stack has been finalised at nine vehicles across four structural families — funds, co-investment channels, business lines and separately managed accounts — calibrated to the duration, cash-flow and governance preferences of institutional capital.",
    related: [{ label: "Fund Architecture", to: "/funds" }],
  },
  {
    slug: "investment-committee-constituted",
    date: "Q1 2026",
    kind: "Investment Committee",
    headline:
      "Investment Committee constituted; underwriting standards, conflicts policy and documented diligence framework adopted.",
    title:
      "Investment Committee constituted; underwriting standards, conflicts policy and documented diligence framework adopted.",
    body:
      "The Investment Committee has been constituted as the single forum of decision, with documented diligence standards, conflicts management policy and recorded deliberation as the operating norm.",
    related: [{ label: "The Partnership", to: "/partnership" }],
  },
  {
    slug: "operating-partner-network-engaged",
    date: "Q4 2025",
    kind: "Operating Network",
    headline:
      "Senior industrial operating partners engaged across the platform's target sectors; advisory framework documented.",
    title:
      "Senior industrial operating partners engaged across the platform's target sectors; advisory framework documented.",
    body:
      "A confidentially curated network of senior industrial operators has been engaged across the partnership's target sectors. The operating partner network supports diligence and value-creation — not in an employee capacity, but on a documented advisory basis.",
    related: [{ label: "Team & Operating Network", to: "/team" }],
  },
  {
    slug: "institutional-infrastructure-engagement",
    date: "Q4 2025",
    kind: "Institutional Infrastructure",
    headline:
      "Engagement of independent administration, custody, audit and compliance counterparties initiated.",
    title:
      "Engagement of independent administration, custody, audit and compliance counterparties initiated.",
    body:
      "Engagement with independent fund administration, custody, audit and compliance counterparties has been initiated to complete the institutional service-provider stack and ensure documented segregation between the partnership and oversight functions.",
    related: [{ label: "Disclosures", to: "/disclosures" }],
  },
  {
    slug: "kellermann-reid-founded",
    date: "Q3 2025",
    kind: "Founding Partnership",
    headline:
      "Kellermann Reid & Co. founded as a senior partnership by Marcel Kellermann and Chris Reid; operating thesis ratified.",
    title:
      "Kellermann Reid & Co. founded as a senior partnership by Marcel Kellermann and Chris Reid.",
    body:
      "The firm was founded as a senior partnership by Marcel Kellermann and Chris Reid, with the operating thesis — disciplined, asset-backed industrial credit and opportunistic industrial equity — ratified at founding.",
    related: [{ label: "The Partnership", to: "/partnership" }],
  },
];

export const NEWS_BY_SLUG = Object.fromEntries(NEWS.map((n) => [n.slug, n]));

export const NEWS_BOILERPLATE =
  "Kellermann Reid & Co. is a private markets investment house in strategic formation, focused on disciplined, asset-backed industrial credit, bridge capital, opportunistic industrial equity and structured capital solutions. Founded by Marcel Kellermann and Chris Reid as a senior partnership, the platform is headquartered in Austin, Texas, with coverage across New York, London, Geneva, Dubai, Singapore and Hong Kong. Investment decisions are made by a documented Investment Committee; the firm does not act as a placement agent or broker-dealer.";
